import { Product } from '@inter-ikea-storageone/range';
import { BatchSortFn } from 'ducks/products';

/*
 * This class is an abstraction over products, clusters and product groups. It is aimed as extensibe high-level
 * helper when implementing functionality that might need to work with different representations of products.
 *
 * The current scenarios we might encounter are mirrored by the BatchType enum:
 * 1. Product - Single product (handles)
 * 2. Cluster - Set of products, a subset of them will be inserted when adding the cluster (frames).
 * 3. Group - Group of products that will be inserted together when adding the group (interior suggestions)
 */

export enum BatchType {
  Product,
  Cluster,
  Group
}

export class ProductBatch {
  product: Product;
  products: Product[];
  type: BatchType;

  static comparison(compare: BatchSortFn | string[], type: BatchType) {
    return (pa: Product | Product[], pb: Product | Product[]) => {
      try {
        const batchA = new ProductBatch(pa, type);
        const batchB = new ProductBatch(pb, type);

        if (Array.isArray(compare)) {
          const idA = batchA.getBestRankedArticleNumber(compare);
          const idB = batchB.getBestRankedArticleNumber(compare);
          const idxA = compare.indexOf(idA);
          const idxB = compare.indexOf(idB);
          return idxA === -1 ? 1 : idxB === -1 ? -1 : idxA - idxB;
        }

        return compare(batchA, batchB);
      } catch {
        return 0;
      }
    };
  }

  constructor(productOrProducts: Product | Product[], type: BatchType) {
    this.products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];
    this.product = this.products[0];
    this.type = type;

    if (!this.product) {
      throw new Error(`Cannot construct product batch from: ${productOrProducts}`);
    }
  }

  private sumAllPrices() {
    return this.products.reduce((acc, curr) => acc + curr.priceInformation.currentPrice, 0);
  }

  getBestRankedArticleNumber(rank: string[]) {
    const articleNos = this.products.map(p => p.globalItemNo);
    return articleNos.reduce(
      (acc, curr) => (rank.includes(curr) && rank.indexOf(curr) < rank.indexOf(acc) ? curr : acc),
      this.product.globalItemNo
    );
  }

  getCheapestProduct() {
    return this.products.reduce(
      (acc, curr) =>
        curr.priceInformation.currentPrice < (acc?.priceInformation.currentPrice ?? Infinity)
          ? curr
          : acc,
      this.product
    );
  }

  getSortingPrice() {
    switch (this.type) {
      case BatchType.Product:
        return this.product.priceInformation.currentPrice;
      case BatchType.Cluster:
        return this.getCheapestProduct().priceInformation.currentPrice;
      case BatchType.Group:
        return this.sumAllPrices();
    }
  }
}
